import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { useMultiSelect } from "../lib/multiSelect";
import MultiSelectBar from "../components/MultiSelectBar";
import { toast } from "sonner";
import { Plus, Trash, Phone, CalendarBlank } from "@phosphor-icons/react";

export default function Meetings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const ms = useMultiSelect();

  const load = async () => setItems((await api.get("/meetings")).data);
  useEffect(() => { load(); }, []);

  const del = async (m) => {
    const ok = await confirm({
      title: "Delete meeting?",
      message: `${m.title}${m.with_party ? ` · with ${m.with_party}` : ""}`,
      tone: "danger",
    });
    if (!ok) return;
    await api.delete(`/meetings/${m.id}`);
    load();
  };

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label="CALENDAR"
        title="Calls & Meetings"
        subtitle="Schedule client calls and on-site meetings."
        action={<Btn onClick={() => setOpen(true)} data-testid="add-meeting"><Plus size={14} /> Schedule</Btn>}
      />

      {items.length === 0 ? (
        <EmptyState title="Nothing scheduled" hint="Add your first call or meeting." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(m => {
            const d = new Date(m.datetime_iso);
            const past = d < new Date();
            return (
              <Card key={m.id} className={`!p-5 ${ms.isSelected(m.id) ? "ring-2 ring-orange-500" : ""}`} data-testid={`meeting-${m.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <input type="checkbox" checked={ms.isSelected(m.id)} onChange={() => ms.toggle(m.id)} className="accent-orange-600" />
                    )}
                    {m.type === "call" ? <Phone size={18} weight="bold" className="text-orange-600" /> : <CalendarBlank size={18} weight="bold" className="text-orange-600" />}
                    <Badge tone={past ? "zinc" : "orange"}>{past ? "past" : m.type}</Badge>
                  </div>
                  <button onClick={() => del(m)} className="text-zinc-400 hover:text-red-600"><Trash size={14} weight="bold" /></button>
                </div>
                <div className="font-display font-bold text-lg leading-tight">{m.title}</div>
                {m.with_party && <div className="text-sm text-zinc-600 mt-1">with {m.with_party}</div>}
                <div className="mt-3 font-mono text-xs text-zinc-500 border-t border-zinc-100 pt-3">
                  {d.toLocaleString("en-IN")}
                </div>
                {m.notes && <p className="text-xs text-zinc-600 mt-2">{m.notes}</p>}
              </Card>
            );
          })}
        </div>
      )}

      {open && <NewModal onClose={() => setOpen(false)} onAdded={() => { setOpen(false); load(); }} />}
      <MultiSelectBar
        count={ms.selected.length}
        ids={ms.selected}
        resource="meetings"
        label="meetings"
        onCleared={() => { ms.clear(); load(); }}
      />
    </div>
  );
}

function NewModal({ onClose, onAdded }) {
  const { confirm } = useConfirm();
  const [f, setF] = useState({
    title: "", type: "meeting", with_party: "", notes: "",
    date: new Date().toISOString().slice(0, 10), time: "10:00",
  });
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    try {
      // master-first prompt for the party
      if (f.with_party.trim()) {
        const [cust, sup] = await Promise.all([
          api.get("/parties", { params: { kind: "customer" } }),
          api.get("/parties", { params: { kind: "supplier" } }),
        ]);
        const all = [...cust.data, ...sup.data];
        const found = all.some(p => p.name.trim().toLowerCase() === f.with_party.trim().toLowerCase());
        if (!found) {
          const kind = await confirm({
            title: `Add "${f.with_party}" as a customer?`,
            message: "They aren't in your Customer or Supplier master.\nAdd them as a Customer now, or skip to just save the meeting.",
            tone: "info",
            confirmLabel: "Add as Customer",
            cancelLabel: "Skip",
          });
          if (kind) {
            try {
              await api.post("/parties", { kind: "customer", name: f.with_party, credit_days: 15 });
              toast.success("Customer added to master");
            } catch (partyErr) {
              console.warn("Failed to add party to master:", partyErr?.response?.data?.detail || partyErr?.message);
            }
          }
        }
      }
      await api.post("/meetings", {
        title: f.title, type: f.type, with_party: f.with_party, notes: f.notes,
        datetime_iso: `${f.date}T${f.time}:00`,
      });
      toast.success("Scheduled");
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal open onClose={onClose} title="Schedule Call or Meeting">
      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="meeting-form">
        <Field label="Title" full><Input value={f.title} onChange={e => upd("title", e.target.value)} required data-testid="meeting-title" /></Field>
        <Field label="Type">
          <Select value={f.type} onChange={e => upd("type", e.target.value)} data-testid="meeting-type">
            <option value="meeting">Meeting</option><option value="call">Call</option>
          </Select>
        </Field>
        <Field label="With (party)"><Input value={f.with_party} onChange={e => upd("with_party", e.target.value)} data-testid="meeting-party" /></Field>
        <Field label="Date"><DatePicker value={f.date} onChange={e => upd("date", e.target.value)} required data-testid="meeting-date" /></Field>
        <Field label="Time"><Input type="time" value={f.time} onChange={e => upd("time", e.target.value)} required data-testid="meeting-time" /></Field>
        <Field label="Notes" full><Input value={f.notes} onChange={e => upd("notes", e.target.value)} /></Field>
        <div className="col-span-2 flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" data-testid="meeting-submit">Schedule</Btn>
        </div>
      </form>
    </Modal>
  );
}
